import siteWorkspaceStyles from './site-workspace.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import { tabbedPageFullscreenStyles } from '../../../styles/tabbed-page-fullscreen.styles';
import { router, resolveRouterPath } from '../../../router';
import {
  getSiteWorkspaceStore,
  SiteWorkspaceStore,
  type SiteWorkspaceState,
} from './site-workspace-store';
import {
  listSiteWorkspaceModules,
  type SiteWorkspaceContext,
  type SiteWorkspaceModuleDescriptor,
} from '../workspace/site-workspace-modules';

import './site-overview.js';
import type { SiteOverview } from './site-overview';
import '../../../components/agwa/agwa-action-bar.js';
import './site-staff-panel.js';
import './site-operation-type-badges.js';

// Import domain module registrations
import '../../../domains/index.js';

import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../../../components/state-badge/agwa-state-badge';
import '../../../components/agwa-page-header';
import '../../../components/layout/agwa-fullscreen-block';
import {
  companiesCrumb,
  companyCrumb,
  companySitesCrumb,
} from '../shared/company-trail';
import { terms } from '../../../language/terms';
import { RecordEditLock } from '../../../utility/locking/record-edit-lock';
import { SITE_LOCK_RESOURCE } from '../shared/site-lock';
import '../../../components/agwa/agwa-lock-held-banner.js';
import '../../../components/agwa/agwa-lock-lost-banner.js';
import {
  tabGroupScrollElement,
  type PageScrollElementHost,
} from '../../../utility/page-scroll-element';

interface SiteTabDefinition {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  section?: string;
  content: TemplateResult;
}

const EMPTY_STATE: SiteWorkspaceState = {
  loading: false,
  error: null,
  site: null,
  mapImageUrl: null,
  ownerName: null,
  ownerEmail: null,
  ownerPhoneNumber: null,
  group: null,
  domains: [],
  isNew: false,
  moduleData: {},
};

type ModuleLoadState = 'idle' | 'loading' | 'loaded' | 'error';

@customElement('site-workspace')
export class SiteWorkspace extends LitElement implements PageScrollElementHost {
  static styles = [
    sharedStyles,
    tabbedPageFullscreenStyles,
    unsafeCSS(siteWorkspaceStyles),
  ];

  /**
   * WHERE THIS PAGE SCROLLS (D-131): the tab group's body, not this host. #371 holds the page
   * header, the banners and the tab strip still and scrolls only the active tab's body, so that
   * is the element the shell starts at the top and hands the keyboard to after a navigation.
   */
  get pageScrollElement(): HTMLElement | null {
    return tabGroupScrollElement(this);
  }

  @property({ type: String }) siteId = '';
  @property({ type: String }) groupId = '';
  @property({ type: String }) tab = '';

  @state() private storeState: SiteWorkspaceState = { ...EMPTY_STATE };
  @state() private activeTabState = 'overview';

  private store: SiteWorkspaceStore | null = null;
  private storeListener: (() => void) | null = null;
  /** Stops the store's owner-contact watch this page holds while it is open. */
  private stopOwnerWatch: (() => void) | null = null;
  private moduleLoadState = new Map<string, ModuleLoadState>();

  /**
   * The site's edit lock (#398 C13a, D-163, @LOCK-040 to 045), held as the company's is: Edit takes
   * it and Save or Cancel releases it, Change Contact holds it around the change. Another person's
   * lock names them and withdraws Edit and Change Contact; the lock this tab held being lost closes
   * an open edit, since it can no longer be saved (@LOCK-044); another person's lock ending re-reads
   * the site, so the values they saved appear here without a reload (@LOCK-041).
   */
  private readonly siteLock = new RecordEditLock(this, SITE_LOCK_RESOURCE, {
    onLost: () => {
      if (this.detailsEditState.editing) {
        this.overviewPanel()?.cancelEdit();
      }
    },
    onOtherReleased: () => void this.store?.refresh(),
  });

  connectedCallback() {
    super.connectedCallback();
    this.attachStore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.detachStore();
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('siteId') || changedProps.has('groupId')) {
      this.attachStore();
    }
    if (changedProps.has('tab')) {
      this.syncActiveTabState();
      void this.showActiveTab();
    } else if (changedProps.has('storeState')) {
      // A deep link to a domain tab arrives before the site's domains have loaded, so the
      // tab it names does not exist when the route's tab is first shown and the group
      // stays on Overview. Once the store delivers the domains the tab renders, and the
      // group has to be told again which tab the route asked for (found by the company
      // page's link to a site's certificates, 2026-09-06).
      void this.showActiveTab();
    }
  }

  private attachStore() {
    this.detachStore();
    // A site not yet created has nothing to lock; any other site is watched while it is shown.
    this.siteLock.watch(this.siteId === 'new' ? '' : this.siteId);
    if (!this.siteId || !this.groupId) {
      this.storeState = { ...EMPTY_STATE };
      return;
    }
    const store = getSiteWorkspaceStore(this.siteId, this.groupId);
    this.store = store;
    this.storeState = { ...store.snapshot };
    this.storeListener = () => {
      this.storeState = { ...store.snapshot };
      this.requestUpdate();
    };
    store.addEventListener('change', this.storeListener);
    // While this page is open, an edit of the site's owner elsewhere redraws their email and phone
    // here without a reload (#398 C4, @CONTACT-011).
    this.stopOwnerWatch = store.watchOwnerContact();
    void store.ensureHydrated();
  }

  private detachStore() {
    if (this.store && this.storeListener) {
      this.store.removeEventListener('change', this.storeListener);
    }
    this.stopOwnerWatch?.();
    this.stopOwnerWatch = null;
    this.storeListener = null;
    this.store = null;
  }

  private ensureModuleLoaded(descriptor: SiteWorkspaceModuleDescriptor) {
    if (!descriptor.load) {
      this.moduleLoadState.set(descriptor.id, 'loaded');
      return;
    }
    const current = this.moduleLoadState.get(descriptor.id);
    if (current === 'loading' || current === 'loaded') {
      return;
    }
    this.moduleLoadState.set(descriptor.id, 'loading');
    descriptor
      .load()
      .then(() => {
        this.moduleLoadState.set(descriptor.id, 'loaded');
        this.requestUpdate();
        // The lazily loaded module may be the tab the route asked for; see updated().
        void this.showActiveTab();
      })
      .catch((error) => {
        console.error(
          `Failed to load site workspace module ${descriptor.id}`,
          error
        );
        this.moduleLoadState.set(descriptor.id, 'error');
        this.requestUpdate();
      });
  }

  private syncActiveTabState() {
    const routeTab = this.tab || 'overview';
    if (this.activeTabState !== routeTab) {
      this.activeTabState = routeTab;
    }
  }

  private async showActiveTab() {
    await this.updateComplete;
    const tabGroup = this.renderRoot.querySelector(
      'sl-tab-group'
    ) as HTMLElement & { show?: (name: string) => void };
    if (tabGroup?.show && this.activeTabState) {
      tabGroup.show(this.activeTabState);
    }
  }

  private handleTabShow(event: CustomEvent<{ name: string }>) {
    const targetTab = event.detail?.name;
    if (!targetTab || targetTab === this.activeTabState) {
      return;
    }
    this.activeTabState = targetTab;
    this.navigateToTab(targetTab);
  }

  private navigateToTab(tabId: string) {
    if (!this.groupId || !this.siteId) {
      return;
    }
    const base = `maintenance/companies/${this.groupId}/sites/${this.siteId}`;
    const suffix = tabId && tabId !== 'overview' ? `/${tabId}` : '';
    void router?.navigate(resolveRouterPath(`${base}${suffix}`));
  }

  /**
   * Go to the company's list of sites.
   *
   * Named for the destination rather than "back", because it is not back: it goes to one fixed
   * place regardless of how this page was reached (architect's ruling, 2026-08-10). Browser Back is
   * what returns a person where they came from, and it still works because this navigates properly.
   */
  private goToSites() {
    if (this.groupId) {
      void router?.navigate(
        resolveRouterPath(`maintenance/companies/${this.groupId}/sites`)
      );
    } else {
      void router?.navigate(resolveRouterPath('maintenance/companies'));
    }
  }

  private buildTabs(): SiteTabDefinition[] {
    const isNew = this.storeState.isNew;
    const baseTabs: SiteTabDefinition[] = [
      {
        id: 'overview',
        label: 'Overview',
        icon: 'house',
        content: html`<site-overview
          @editing-changed=${this.handleEditingChanged}
          .site=${this.storeState.site}
          .mapImageUrl=${this.storeState.mapImageUrl}
          .ownerName=${this.storeState.ownerName}
          .ownerEmail=${this.storeState.ownerEmail}
          .ownerPhoneNumber=${this.storeState.ownerPhoneNumber}
          .domains=${this.storeState.domains}
          .loading=${this.storeState.loading}
          .isNew=${isNew}
          .groupId=${this.groupId}
          .store=${this.store}
          .editLock=${isNew ? null : this.siteLock}
          .lockedByOther=${this.siteLock.lockedByOther}
        ></site-overview>`,
      },
      {
        id: 'staff',
        label: `${terms.staffUser.value} Assignment`,
        icon: 'people',
        disabled: isNew,
        content: html`<div class="tab-panel-body">
          <site-staff-panel
            .siteId=${this.siteId}
            .groupId=${this.groupId}
            .siteName=${this.storeState.site?.name ?? ''}
          ></site-staff-panel>
        </div>`,
      },
    ];

    // Add dynamic domain-based modules
    if (!isNew) {
      const dynamicModules = listSiteWorkspaceModules(this.storeState.domains);
      const context: SiteWorkspaceContext = {
        site: this.storeState.site,
        group: this.storeState.group,
        domains: this.storeState.domains,
        loading: this.storeState.loading,
        siteId: this.siteId,
        groupId: this.groupId,
      };

      for (const mod of dynamicModules) {
        this.ensureModuleLoaded(mod);
        const state = this.moduleLoadState.get(mod.id) ?? 'idle';
        let content: TemplateResult;
        if (state === 'error') {
          content = html`<div class="tab-panel-body">
            <sl-alert variant="danger" open>
              Unable to load ${mod.label}. Check the console for details.
            </sl-alert>
          </div>`;
        } else if (state === 'loading' || (mod.load && state !== 'loaded')) {
          content = html`<div class="tab-panel-body">
            <div class="module-loading">
              <sl-spinner></sl-spinner>
              <span>Loading ${mod.label}…</span>
            </div>
          </div>`;
        } else {
          content = html`<div class="tab-panel-body">
            ${mod.render(context)}
          </div>`;
        }
        baseTabs.push({
          id: mod.id,
          label: mod.label,
          icon: mod.icon,
          disabled: false,
          section: mod.section,
          content,
        });
      }
    }

    return baseTabs;
  }

  /**
   * `Companies > {company} > All Sites > {site}` (the trail standard, architect 2026-09-06),
   * the site as the leaf with what it is and whether it is operating beside it. The company
   * crumb waits for the company to load; the list crumbs need only the id in the route.
   */
  private siteCrumbs() {
    const group = this.storeState.group;
    return [
      companiesCrumb(),
      ...(group ? [companyCrumb({ id: this.groupId, name: group.name })] : []),
      companySitesCrumb(this.groupId),
    ];
  }

  private renderHeader() {
    const site = this.storeState.site;
    const isNew = this.storeState.isNew;
    return html`
      <agwa-page-header class="nav-header" .crumbs=${this.siteCrumbs()}>
        ${
          isNew
            ? html`New ${terms.loginSite.value}`
            : site
              ? html`
                  ${site.name}
                  <span slot="badges" class="site-badges">
                    <!--
                        WHAT THE SITE IS, first among its badges. The codes are part of how the
                        people using this refer to a site ("PGM Test Site 1c"), so they lead the
                        run. Renders nothing at all when the site holds no type, so a site
                        with none is not left with a gap.
                      -->
                    <site-operation-type-badges
                      .domains=${this.storeState.domains}
                    ></site-operation-type-badges>
                    <!--
                        The one home of the site's operating status (architect, 2026-09-05: the
                        details card said the name and the status twice over). The header owns
                        identity and operational state, exactly as the member banner does; the
                        details card below holds only the record.
                      -->
                    <agwa-state-badge
                      domain="siteOperating"
                      state=${site.active ? 'Active' : 'Inactive'}
                    ></agwa-state-badge>
                  </span>
                `
              : nothing
        }
        ${this.renderActionBar()}
      </agwa-page-header>
    `;
  }

  private renderTabNav(tabs: SiteTabDefinition[]) {
    const result: TemplateResult[] = [];
    const seenSections = new Set<string>();
    // A section label earns its place only when it groups something: over a single tab it
    // repeats what the tab already says (architect, 2026-09-06). The registrations keep their
    // sections regardless, so the label returns by itself once a second module joins one.
    const tabsPerSection = new Map<string, number>();
    for (const tab of tabs) {
      if (tab.section) {
        tabsPerSection.set(
          tab.section,
          (tabsPerSection.get(tab.section) ?? 0) + 1
        );
      }
    }

    for (const tab of tabs) {
      if (
        tab.section &&
        !seenSections.has(tab.section) &&
        (tabsPerSection.get(tab.section) ?? 0) > 1
      ) {
        seenSections.add(tab.section);
        result.push(html`
          <div slot="nav" class="tab-section-header">${tab.section}</div>
        `);
      }
      result.push(html`
        <sl-tab slot="nav" panel=${tab.id} ?disabled=${tab.disabled}>
          ${
            tab.icon
              ? html`<sl-icon
                  name=${tab.icon}
                  style="margin-right: 0.5rem;"
                ></sl-icon>`
              : nothing
          }
          ${tab.label}
        </sl-tab>
      `);
    }
    return result;
  }

  private renderTabs() {
    const tabs = this.buildTabs();
    return html`
      <sl-tab-group class="tab-shell" @sl-tab-show=${this.handleTabShow}>
        ${this.renderTabNav(tabs)}
        ${tabs.map(
          (t) => html`<sl-tab-panel name=${t.id}>${t.content}</sl-tab-panel>`
        )}
      </sl-tab-group>
    `;
  }

  private renderLoading() {
    return html`
      <div class="loading-container">
        <sl-spinner style="font-size: 2rem;"></sl-spinner>
        <p>Loading ${terms.loginSite.lower}...</p>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="error-container">
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.storeState.error || `An error occurred loading the ${terms.loginSite.lower}.`}
        </sl-alert>
        <sl-button variant="primary" @click=${this.goToSites}>
          <sl-icon slot="prefix" name="arrow-left"></sl-icon>
          ${terms.loginSite.plural}
        </sl-button>
      </div>
    `;
  }

  /** Mirrors site-overview's edit state so the bar can swap its actions. */
  @state() private detailsEditState = { editing: false, saving: false };

  private handleEditingChanged(
    e: CustomEvent<{ editing: boolean; saving: boolean }>
  ) {
    this.detailsEditState = e.detail;
  }

  private overviewPanel(): SiteOverview | null {
    return this.renderRoot.querySelector('site-overview');
  }

  /**
   * The site's action strip, completing the one action grammar across product, company and
   * site (architect, 2026-09-05: "the edit and save actions are moved there instead of
   * being in the body... consistency will make the experience easier"). Edit offers itself
   * on the Overview tab; while editing - including NEW-site creation, which auto-enters
   * editing - Save/Cancel replace it. The bar's own confirm machinery stays unused; the
   * overview keeps its validation messaging in the body where the fields are.
   */
  private renderActionBar() {
    const isNew = this.siteId === 'new';
    if (!this.storeState.site && !isNew) return nothing;
    // While another person edits this site its Edit is withdrawn (@LOCK-040): the banner says who,
    // and the edit would be refused by the lock anyway. As the company page does.
    if (this.siteLock.lockedByOther && !this.detailsEditState.editing) {
      return nothing;
    }
    const onOverview = (this.activeTabState || 'overview') === 'overview';
    const actions = this.detailsEditState.editing
      ? [
          {
            id: 'save-details',
            label: 'Save Changes',
            icon: 'check2',
            variant: 'primary' as const,
          },
          { id: 'cancel-edit', label: 'Cancel' },
        ]
      : onOverview
        ? [{ id: 'edit-details', label: 'Edit', icon: 'pencil' }]
        : [];
    if (!actions.length) return nothing;
    // Inline in the header's actions slot, right of the trail, the same line the company
    // workspace uses (architect, 2026-09-06): the framed row under the header cost a row.
    return html`<agwa-action-bar
      inline
      slot="actions"
      .loading=${this.detailsEditState.saving}
      .actions=${actions}
      @action=${this.handleActionBarAction}
    ></agwa-action-bar>`;
  }

  /**
   * Edit takes the site's lock first and opens the form only once it is held, on the site as read
   * after taking it, so the edit starts from what is saved now rather than from a copy older than
   * somebody else's save; a re-read that leaves no site lets the lock go rather than holding it with
   * no form open. Save releases it only after the save landed; Cancel writes nothing and releases
   * it (@LOCK-040 to 042). A new site has no lock: it does not exist to be edited by two people.
   */
  private async handleActionBarAction(e: CustomEvent<{ id: string }>) {
    const isNew = this.siteId === 'new';
    switch (e.detail.id) {
      case 'edit-details':
        if (await this.siteLock.take()) {
          let opened = false;
          try {
            await this.store?.refresh();
            await this.updateComplete;
            await this.overviewPanel()?.updateComplete;
            if (this.storeState.site) {
              this.overviewPanel()?.startEdit();
              opened = this.detailsEditState.editing;
            }
          } finally {
            if (!opened) await this.siteLock.release();
          }
        }
        break;
      case 'save-details':
        if ((await this.overviewPanel()?.saveEdit()) && !isNew) {
          await this.siteLock.release();
        }
        break;
      case 'cancel-edit':
        this.overviewPanel()?.cancelEdit();
        if (!isNew) await this.siteLock.release();
        break;
    }
  }

  /**
   * The site lock's two banners, in the wording every lockable page uses: the lock this tab held is
   * gone (@LOCK-044), or somebody else is editing this site (@LOCK-040). About the site, not a tab,
   * so they sit under the header with it.
   */
  private renderSiteLockBanners() {
    return html`
      ${
        this.siteLock.loss
          ? html`<agwa-lock-lost-banner
              noun=${terms.loginSite.lower}
              taken-over-by-user-id=${this.siteLock.loss.takenOverByUserId ?? ''}
              @dismiss=${() => this.siteLock.dismissLoss()}
            ></agwa-lock-lost-banner>`
          : nothing
      }
      ${
        this.siteLock.lockedByOther
          ? html`<agwa-lock-held-banner
              noun=${terms.loginSite.lower}
              holder-user-id=${this.siteLock.holderUserId ?? ''}
              .leaseExpiresAt=${this.siteLock.expiresAt}
            ></agwa-lock-held-banner>`
          : nothing
      }
    `;
  }

  render() {
    // Show loading on initial load (no site yet)
    if (this.storeState.loading && !this.storeState.site) {
      return html`
        <div class="workspace-root">
          ${this.renderHeader()} ${this.renderLoading()}
        </div>
      `;
    }

    // Show error if we have one and no site
    if (this.storeState.error && !this.storeState.site) {
      return html`
        <div class="workspace-root">
          ${this.renderHeader()} ${this.renderError()}
        </div>
      `;
    }

    return html`
      <div class="workspace-root">
        <!--
          THE TAB GROUP IS ONE BLOCK THAT CAN TAKE THE WHOLE VIEWPORT IN PLACE (#373, D-106).
          The header rides in the wrapper's header slot: the trail out of the site, its name,
          what it is and the Edit / Save actions are about the SITE rather than about a tab, so
          they stay on screen inside the takeover, and a form being edited keeps its controls.
        -->
        <agwa-fullscreen-block
          class="page-takeover"
          .title=${terms.loginSite.value}
        >
          <div class="page-chrome" slot="header">
            ${this.renderHeader()} ${this.renderSiteLockBanners()}
          </div>
          ${this.renderTabs()}
        </agwa-fullscreen-block>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'site-workspace': SiteWorkspace;
  }
}
